/**
 * Text utility functions for the Lightway app
 * Provides URL detection and linkification for text content
 */

/**
 * Regular expression to match URLs in text
 * Matches:
 * - http:// and https:// URLs
 * - www. URLs (will be prefixed with https://)
 */
const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

/**
 * Converts text containing URLs into an array of text and link objects
 * for rendering in JSX
 *
 * @param {string} text - The text to process
 * @returns {Array} Array of strings and link objects
 *
 * @example
 * linkifyTextToJSX("Check out https://example.com for more info")
 * // Returns: ["Check out ", { type: 'link', key: 'link-0', href: 'https://example.com', text: 'https://example.com' }, " for more info"]
 */
export const linkifyTextToJSX = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const parts = [];
  let lastIndex = 0;
  let match;
  let linkIndex = 0;

  // Reset regex lastIndex for global search
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Get the URL and ensure it has a protocol
    let url = match[0];
    let href = url;

    // Add https:// to www. URLs
    if (url.toLowerCase().startsWith('www.')) {
      href = 'https://' + url;
    }

    // Remove trailing punctuation that's likely not part of the URL
    const trailingPunctuation = /[.,;:!?)]+$/;
    const punctuationMatch = url.match(trailingPunctuation);
    let trailingChars = '';

    if (punctuationMatch) {
      trailingChars = punctuationMatch[0];
      url = url.slice(0, -trailingChars.length);
      href = href.slice(0, -trailingChars.length);
    }

    // Add the link object
    parts.push({
      type: 'link',
      key: `link-${linkIndex}`,
      href: href,
      text: url
    });

    // Add back any trailing punctuation as regular text
    if (trailingChars) {
      parts.push(trailingChars);
    }

    lastIndex = match.index + match[0].length - trailingChars.length;
    linkIndex++;
  }

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no URLs found, return the original text as a single element
  if (parts.length === 0) {
    parts.push(text);
  }

  return parts;
};

/**
 * Check if a string contains any URLs
 *
 * @param {string} text - The text to check
 * @returns {boolean} True if the text contains URLs
 */
export const containsUrl = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
};

const textUtils = {
  linkifyTextToJSX,
  containsUrl
};

export default textUtils;
